/* ══════════════════════════════════════════════════════════════
   Phase 5A — Chat UI module
   Wires the floating chat bubble + .window-chat panel to /api/chat.
   - Anchors bubble/panel to the dock via CSS custom properties.
   - Streams SSE responses token-by-token.
   - Keeps last 6 turns of history in memory only.
   ══════════════════════════════════════════════════════════════ */

const HISTORY_LIMIT = 6;
const MAX_HISTORY_FOR_REQUEST = 6;

let isStreaming = false;
const history = []; // [{ role: 'user' | 'assistant', content: string }]

/* ── DOCK ANCHOR ───────────────────────────────────────────────
   The chat bubble's vertical position is computed in CSS as
     bottom: --dock-bottom + (--dock-height - 56) / 2
   so it sits centered on the dock. We have to keep those custom
   properties in sync with the dock's *resting* layout — never with
   transforms applied during the desktop slide-in/out.

   getBoundingClientRect() returns transform-affected values, so if
   we measure mid-animation we'd write the wrong --dock-bottom and
   the bubble would visibly jump when something else (like the
   user hovering the dock and triggering its rubbery resize observer)
   re-runs sync. To avoid that we bail when the dock is hidden or
   transformed, leaving the previous (correct) values in place. */
function isDockMeasurable(dock, rect) {
  if (!rect.width || !rect.height) return false;
  /* While #view-desktop carries any transition-state class the dock
     is mid-flight; its rect.bottom can be far from its at-rest value.
     Wait until the desktop is at rest before reading. */
  let p = dock.parentElement;
  while (p) {
    if (p.id === 'view-desktop') {
      if (p.classList.contains('is-sliding-down')
          || p.classList.contains('is-sliding-up')
          || p.classList.contains('is-slid-down')
          || p.classList.contains('is-fading-in')
          || p.classList.contains('is-fading-out')
          || p.classList.contains('is-faded-out')
          || p.classList.contains('is-hidden')
          || p.classList.contains('is-transitioning')) {
        return false;
      }
      break;
    }
    p = p.parentElement;
  }
  return true;
}

function syncDockAnchor() {
  const dock = document.getElementById('dock');
  if (!dock) return;
  const r = dock.getBoundingClientRect();
  if (!isDockMeasurable(dock, r)) return;
  const root = document.documentElement;
  root.style.setProperty('--dock-right', `${r.right}px`);
  root.style.setProperty('--dock-bottom', `${Math.max(0, window.innerHeight - r.bottom)}px`);
  root.style.setProperty('--dock-top', `${r.top}px`);
  root.style.setProperty('--dock-height', `${r.height}px`);
}

/* ── DOM HELPERS ─────────────────────────────────────────────── */

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function appendMessage(log, role, content) {
  const cls = role === 'user'
    ? 'chat-msg chat-msg-user'
    : role === 'system'
      ? 'chat-msg chat-msg-system'
      : 'chat-msg chat-msg-bot';
  const node = el('div', cls, content);
  log.appendChild(node);
  log.scrollTop = log.scrollHeight;
  return node;
}

function appendTyping(log) {
  const wrap = el('div', 'chat-typing');
  wrap.appendChild(el('span'));
  wrap.appendChild(el('span'));
  wrap.appendChild(el('span'));
  log.appendChild(wrap);
  log.scrollTop = log.scrollHeight;
  return wrap;
}

/* ── PANEL TOGGLE ────────────────────────────────────────────── */

/* The chat panel reuses .window markup, so the existing window-manager
   close handler also fires on our title-btn.close — that handler runs
   animatedClose() and ends with `classList.add('minimized')`. We have
   to live alongside it: when opening, strip every residual class the
   window manager might have left so the panel renders cleanly. */
const RESIDUAL_WINDOW_CLASSES = [
  'minimized', 'win-closing', 'win-opening', 'shaded', 'active',
];

/* Mirrors the window manager's isAnimatedTheme() — themes for which the
   global open/close keyframes (winOpen / winClose / 2077 glitch) play.
   The chat panel reuses those same classes so it gets the same feel. */
const ANIMATED_THEMES = new Set(['current', '2007', '2077']);
function isAnimatedTheme() {
  /* Skip the animation on touch / coarse-pointer devices to match the
     window manager's `!isMobile` gate (the legacy themes look wrong at
     mobile sizes and the dock-anchored chat sheet has its own slide). */
  const coarse = matchMedia?.('(pointer: coarse)').matches;
  if (coarse) return false;
  return ANIMATED_THEMES.has(document.documentElement.dataset.theme);
}

function bringChatToFront(panel) {
  /* The window manager keeps its z-index counter in a private scope, so
     we can't call its bringToFront(). Compute the current top z-index
     across all .window elements and put the chat panel one above. Also
     mirror the manager's behavior of stripping .active from siblings. */
  let max = 10;
  document.querySelectorAll('.window').forEach((w) => {
    if (w === panel) return;
    w.classList.remove('active');
    const z = parseInt(getComputedStyle(w).zIndex, 10);
    if (Number.isFinite(z) && z > max) max = z;
  });
  panel.style.zIndex = String(max + 1);
  panel.classList.add('active');
}

function openPanel(panel, bubble, input) {
  panel.classList.remove(...RESIDUAL_WINDOW_CLASSES);
  /* Reset all inline geometry/animation styles so the panel always
     snaps back to its CSS-defined dock-anchored position, regardless
     of any drag, resize, or close-animation residue from earlier. */
  ['left', 'top', 'right', 'bottom', 'width', 'height',
   'transform', 'transition', 'height']
    .forEach((prop) => panel.style.removeProperty(prop));
  panel.style.removeProperty('--gx-rx');
  panel.style.removeProperty('--gx-ry');
  panel.hidden = false;
  bringChatToFront(panel);
  bubble.setAttribute('aria-expanded', 'true');

  /* Match the desktop windows' open animation. The `.win-opening` class
     drives the global @keyframes winOpen (current theme) or the 2077
     glitch keyframe — same fade-in/scale you see when launching gallery,
     contact, etc. from the dock. Strip the class on animationend so we
     don't leave it dangling. The CSS is gated by theme already. */
  if (isAnimatedTheme()) {
    panel.classList.add('win-opening');
    panel.addEventListener('animationend', () => {
      panel.classList.remove('win-opening');
    }, { once: true });
  }

  setTimeout(() => input?.focus(), 50);
}

function closePanel(panel, bubble) {
  bubble.setAttribute('aria-expanded', 'false');

  /* Mirror animatedClose() from the window manager: play `.win-closing`
     (winClose keyframe in current/2007, glitch in 2077), then commit the
     hidden state on animationend. In non-animated themes, skip straight
     to hidden so we don't introduce a stutter on legacy looks. */
  if (isAnimatedTheme()) {
    panel.classList.add('win-closing');
    panel.addEventListener('animationend', () => {
      panel.classList.remove('win-closing');
      panel.hidden = true;
      panel.classList.remove(...RESIDUAL_WINDOW_CLASSES);
    }, { once: true });
  } else {
    panel.hidden = true;
    panel.classList.remove(...RESIDUAL_WINDOW_CLASSES);
  }
}

function isPanelVisible(panel) {
  /* Treat `hidden`, `.minimized` (window-manager's close-button path),
     and `.win-closing` (mid-close animation) all as "closed". Without
     the win-closing check, a second bubble click during the 0.2s close
     animation would re-trigger openPanel mid-fade-out. */
  if (panel.hidden) return false;
  if (panel.classList.contains('minimized')) return false;
  if (panel.classList.contains('win-closing')) return false;
  return true;
}

/* ── REQUEST / SSE ───────────────────────────────────────────── */

async function sendMessage(message, refs) {
  if (!message || isStreaming) return;
  const { log, suggestions, input, sendBtn, form, setAvatarState } = refs;

  suggestions.hidden = true;
  appendMessage(log, 'user', message);
  history.push({ role: 'user', content: message });

  isStreaming = true;
  input.disabled = true;
  sendBtn.disabled = true;
  input.value = '';

  /* Drives the 2026 glass-orb avatar. `firstChunk` ensures we flip
     to 'talking' exactly once when the first SSE token arrives,
     instead of thrashing data-state on every chunk. */
  setAvatarState?.('thinking');
  let firstChunk = true;

  const typing = appendTyping(log);
  let botNode = null;
  let botText = '';

  const fail = (msg) => {
    if (typing.parentNode) typing.remove();
    if (!botNode) appendMessage(log, 'system', msg);
  };

  try {
    const res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        history: history.slice(-MAX_HISTORY_FOR_REQUEST - 1, -1)
      })
    });

    const ctype = res.headers.get('content-type') || '';

    if (!res.ok || ctype.includes('application/json')) {
      let errText = 'Something went wrong. Try again in a moment.';
      try {
        const data = await res.json();
        if (data && data.error) errText = data.error;
      } catch { /* keep default */ }
      fail(errText);
      return;
    }

    if (!ctype.includes('text/event-stream') || !res.body) {
      fail("Hmm, I couldn't read the response. Please try again.");
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const ensureBotNode = () => {
      if (!botNode) {
        if (typing.parentNode) typing.remove();
        botNode = appendMessage(log, 'assistant', '');
      }
      return botNode;
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (!payload) continue;
        if (payload === '[DONE]') {
          buffer = '';
          break;
        }
        try {
          const chunk = JSON.parse(payload);
          const token = typeof chunk.response === 'string'
            ? chunk.response
            : (typeof chunk.token === 'string' ? chunk.token : '');
          if (token) {
            if (firstChunk) {
              setAvatarState?.('talking');
              firstChunk = false;
            }
            botText += token;
            const node = ensureBotNode();
            node.textContent = botText;
            log.scrollTop = log.scrollHeight;
          }
        } catch { /* skip malformed event */ }
      }
    }

    if (botText) {
      history.push({ role: 'assistant', content: botText });
      // Trim history defensively.
      while (history.length > HISTORY_LIMIT * 2) history.shift();
    } else {
      fail("I didn't receive a response. Please try again.");
    }
  } catch (err) {
    fail('Network hiccup — please try again.');
  } finally {
    if (typing.parentNode) typing.remove();
    isStreaming = false;
    input.disabled = false;
    sendBtn.disabled = false;
    input.focus();
    /* Single idle reset covers every exit path: stream end, [DONE],
       JSON error, 429, network hiccup, empty response. */
    setAvatarState?.('idle');
  }
}

/* ── INIT ────────────────────────────────────────────────────── */

export function initChat() {
  const bubble = document.getElementById('chat-bubble');
  const panel = document.getElementById('win-chat');
  const log = document.getElementById('chat-log');
  const suggestions = document.getElementById('chat-suggestions');
  const form = document.getElementById('chat-form');
  const input = document.getElementById('chat-input');
  const sendBtn = document.getElementById('chat-send');

  if (!bubble || !panel || !log || !form || !input || !sendBtn) return;

  /* The avatar is only present in the 2026 theme. The setter no-ops
     when the element is missing, so other eras keep working. */
  const avatar = bubble.querySelector('.chat-avatar');
  const setAvatarState = (state) => {
    if (avatar) avatar.dataset.state = state;
  };

  const refs = { log, suggestions, input, sendBtn, form, setAvatarState };

  // Initial anchor sync + keep it in sync.
  syncDockAnchor();
  window.addEventListener('resize', syncDockAnchor);

  const dock = document.getElementById('dock');
  if (dock && typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(syncDockAnchor);
    ro.observe(dock);
  }

  const themeObserver = new MutationObserver(() => {
    // Theme change can resize dock / fonts — recompute.
    requestAnimationFrame(syncDockAnchor);
  });
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  });

  /* When the user navigates between /work and the desktop, the router
     toggles transition classes on #view-desktop (is-sliding-up,
     is-fading-in, is-hidden, etc.). The dock's *size* doesn't change
     during the slide so the ResizeObserver above doesn't fire — but
     the dock's at-rest position does change relative to whatever
     stale values --dock-bottom currently holds (e.g. fallbacks if the
     bubble was never measured, or values from before the user
     navigated to /work). Re-sync once the desktop is back at rest so
     the bubble snaps to its centered position immediately, instead of
     waiting for the user to hover the dock and trip its resize
     observer. */
  const viewDesktop = document.getElementById('view-desktop');
  if (viewDesktop) {
    const desktopObserver = new MutationObserver(() => {
      /* Two RAFs: first to let the class change paint, second to
         catch the post-transitionend layout. syncDockAnchor itself
         bails if the desktop is still mid-animation. */
      requestAnimationFrame(() => requestAnimationFrame(syncDockAnchor));
    });
    desktopObserver.observe(viewDesktop, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  // Toggle panel.
  bubble.addEventListener('click', () => {
    if (isPanelVisible(panel)) closePanel(panel, bubble);
    else openPanel(panel, bubble, input);
  });

  // Close button: the existing window manager already runs animatedClose()
  // + classList.add('minimized') on click. We just need to keep our own
  // bubble state in sync and reset the `hidden` attribute so we behave
  // consistently with the open-state check on the next bubble click.
  const closeBtn = panel.querySelector('.title-btn.close');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      bubble.setAttribute('aria-expanded', 'false');
      /* `hidden` is left off so the window manager's close animation
         can play. We reapply `hidden` after the animation ends below. */
    });
  }
  /* When the window manager finishes its close animation it adds
     .minimized; mirror that into our `hidden` attribute so the bubble
     toggle uses a consistent signal next time. */
  if (typeof MutationObserver !== 'undefined') {
    const minObs = new MutationObserver(() => {
      if (panel.classList.contains('minimized') && !panel.hidden) {
        panel.hidden = true;
      }
    });
    minObs.observe(panel, { attributes: true, attributeFilter: ['class'] });
  }

  // Stop drag handlers from picking up the chat title bar.
  const titleBar = panel.querySelector('.title-bar');
  if (titleBar) {
    const stop = (e) => e.stopPropagation();
    titleBar.addEventListener('pointerdown', stop);
    titleBar.addEventListener('mousedown', stop);
    titleBar.addEventListener('touchstart', stop, { passive: true });
  }

  // Suggestion chips → submit immediately.
  suggestions?.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.classList.contains('chat-chip')) return;
    const text = target.textContent?.trim();
    if (!text) return;
    sendMessage(text, refs);
  });

  // Form submit.
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const msg = input.value.trim();
    if (!msg) return;
    sendMessage(msg, refs);
  });
}

export default initChat;
